import React from 'react';
import type { CallPutEnum, FrequencyEnum, OptionData } from 'venn-api';
import { capitalize } from 'lodash';
import { OptionIcon } from './OptionIcon';
import { formatData } from '../data-grid/gridUtils';
import { Dates, useHasFF } from 'venn-utils';
import { parseOptionSymbol } from '../../../venn-utils/src/options/utils';
import GenericRangeTooltipContent from './GenericRangeTooltipContent';

type OptionTooltipContentProps = Readonly<{
  name: string;
  optionData: OptionData;
  investmentTimeRange: string | undefined;
  investmentSymbol: string | undefined;
  frequency: FrequencyEnum | undefined;
  footer?: React.ReactNode;
}>;

const OptionType = ({ optionType }: { optionType: CallPutEnum }) => {
  if (optionType === 'UNKNOWN') {
    return '--';
  }

  return (
    <div className="flex flex-row gap-[4px] font-bold">
      <OptionIcon optionType={optionType} inverted />
      <span>{optionType === 'CALL' ? 'Call' : 'Put'}</span>
    </div>
  );
};

export const OptionTooltipContent = ({
  name,
  optionData,
  investmentTimeRange,
  investmentSymbol,
  frequency,
  footer,
}: OptionTooltipContentProps) => {
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');
  // Option symbols are in OCC format, e.g. "AAPL 230616C1500000"
  // We want to extract the underlying investment symbol (e.g. "AAPL") to display in the tooltip
  const symbol = parseOptionSymbol(investmentSymbol)?.underlyingSymbol;

  return (
    <GenericRangeTooltipContent
      name={name}
      symbol={symbol}
      footer={footer}
      rows={[
        {
          label: 'Option Type',
          content: <OptionType optionType={optionData.optionType} />,
          dataTestId: 'option-type',
          id: 'option-type',
        },
        {
          label: 'Expiration Date',
          content: Dates.toDDMMMYYYY(optionData.expiration, 'DAILY'),
          dataTestId: 'expiration-date',
          id: 'expiration-date',
        },
        {
          label: 'Strike Price',
          content: formatData({ num: optionData.strike, dataType: 'CURRENCY', skipKSuffixInFormatting }),
          dataTestId: 'strike-price',
          id: 'strike-price',
        },
        {
          label: 'Returns Availability Range',
          content: investmentTimeRange ?? '--',
          dataTestId: 'investment-range',
          id: 'returns-availability-range',
        },
        {
          label: 'Return Frequency',
          content: capitalize(frequency ?? '--'),
          dataTestId: 'return-frequency',
          id: 'return-frequency',
        },
      ]}
    />
  );
};
